// src/pages/Register.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  UserPlus, Mail, Lock, User, Building2, AlertCircle, ArrowRight, ShieldCheck, Loader2, Eye, EyeOff, Recycle 
} from 'lucide-react';

export default function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'volunteer',
    ngoCertificationCode: '',
    adminSecretKey: '',
    agentSecretKey: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']); // 6 separate digits
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState('password'); // 'password' | 'otp'
  const [otpSent, setOtpSent] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);

  const { register, checkAuth, api } = useAuth();
  const navigate = useNavigate();

  // Auto-focus first OTP digit when OTP mode starts
  useEffect(() => {
    if (authMode === 'otp' && otpSent) {
      document.getElementById('otp-0')?.focus();
    }
  }, [authMode, otpSent]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Full name is required');
      toast.error('Full name is required');
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      toast.error('Email is required');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      toast.error('Password must be at least 6 characters');
      return;
    }

    // Validate authorization codes
    if (formData.role === 'ngo' && !formData.ngoCertificationCode.trim()) {
      setError('NGO Certification Code is required');
      toast.error('Please enter your NGO Certification Code');
      return;
    }

    if (formData.role === 'admin' && !formData.adminSecretKey.trim()) {
      setError('Admin Master Key is required');
      toast.error('Please enter the Admin Master Key');
      return;
    }

    if (formData.role === 'agent' && !formData.agentSecretKey.trim()) {
      setError('Agent Access Key is required');
      toast.error('Please enter the Agent Access Key');
      return;
    }

    setIsLoading(true);

    try {
      await register({
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password.trim(),
        role: formData.role,
        ngoCertificationCode: formData.ngoCertificationCode.trim(),
        adminSecretKey: formData.adminSecretKey.trim(),
        agentSecretKey: formData.agentSecretKey.trim(),
      });
      setVerificationEmailSent(true);
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Registration failed.';

      if (msg.includes('disposable') || msg.includes('temporary')) {
        toast.error('Temporary or disposable emails not allowed.');
      } else if (msg.includes('invalid') || msg.includes('domain')) {
        toast.error('Invalid email domain.');
      } else if (msg.includes('already registered')) {
        toast.error('Email already registered. Please login.');
      } else {
        toast.error(msg);
      }

      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const emailTrim = formData.email.trim();
    if (!emailTrim) {
      toast.error('Email is required');
      return;
    }

    setIsLoading(true);
    try {
      const res = await api.post('/api/auth/resend-verification', { email: emailTrim });
      toast.success(res.data?.message || 'Verification link sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend verification link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const nameTrim = formData.name.trim();
    const emailTrim = formData.email.trim();

    if (!nameTrim || !emailTrim) {
      setError('Name and email are required');
      toast.error('Name and email are required');
      return;
    }

    // Validate authorization codes
    if (formData.role === 'ngo' && !formData.ngoCertificationCode.trim()) {
      setError('NGO Certification Code is required');
      toast.error('Please enter your NGO Certification Code');
      return;
    }

    if (formData.role === 'admin' && !formData.adminSecretKey.trim()) {
      setError('Admin Master Key is required');
      toast.error('Please enter the Admin Master Key');
      return;
    }

    if (formData.role === 'agent' && !formData.agentSecretKey.trim()) {
      setError('Agent Access Key is required');
      toast.error('Please enter the Agent Access Key');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await api.post('/api/auth/send-otp', { email: emailTrim });
      setOtpSent(true);
      toast.success('OTP sent! Check your email (including spam).');
      setError('');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP.';

      if (msg.includes('disposable') || msg.includes('temporary')) {
        toast.error('Temporary/disposable emails not allowed.');
      } else if (msg.includes('invalid') || msg.includes('domain')) {
        toast.error('Invalid email domain.');
      } else {
        toast.error(msg);
      }

      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const cleanOtp = otp.join('');
    if (cleanOtp.length !== 6) {
      setError('Enter a valid 6-digit OTP');
      toast.error('Invalid OTP format');
      return;
    }

    // Validate authorization codes
    if (formData.role === 'ngo' && !formData.ngoCertificationCode.trim()) {
      setError('NGO Certification Code is required');
      toast.error('Please enter your NGO Certification Code');
      return;
    }

    if (formData.role === 'admin' && !formData.adminSecretKey.trim()) {
      setError('Admin Master Key is required');
      toast.error('Please enter the Admin Master Key');
      return;
    }

    if (formData.role === 'agent' && !formData.agentSecretKey.trim()) {
      setError('Agent Access Key is required');
      toast.error('Please enter the Agent Access Key');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      await api.post('/api/auth/verify-otp', { 
        email: formData.email.trim(), 
        otp: cleanOtp,
        role: formData.role,
        ngoCertificationCode: formData.ngoCertificationCode.trim(),
        adminSecretKey: formData.adminSecretKey.trim(),
        agentSecretKey: formData.agentSecretKey.trim(),
      });

      // AuthContext will show a success toast on successful OTP verification
      await checkAuth();
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid or expired OTP';
      toast.error(msg);
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (value.length > 1) value = value[0]; // single digit only
    if (!/^\d*$/.test(value)) return; // only digits

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next field
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const onboardingHighlights = [
    'Choose the right role and get a dashboard tailored to your workflow.',
    'Create your account with password signup or quick email OTP verification.',
    'Start exploring opportunities, messaging, and pickup coordination immediately.',
  ];

  const inputWithIconClass = 'block w-full rounded-2xl border border-slate-300 bg-white pl-11 pr-4 py-3 text-slate-900 shadow-sm transition-all duration-200 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100';
  const primaryButtonClass = 'w-full flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-6 py-3.5 text-white font-semibold shadow-sm transition-all duration-200 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-200/60 disabled:cursor-not-allowed disabled:opacity-60 active:scale-[0.98]';
  const otpBoxClass = 'h-12 w-12 rounded-xl border border-slate-300 bg-white text-center text-2xl font-mono text-slate-900 shadow-sm transition-all duration-200 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100';

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 px-4 py-10 sm:px-6 lg:px-8">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.14),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.12),transparent_24%)]" />
      <div className="relative mx-auto flex min-h-[calc(100vh-5rem)] max-w-5xl items-center">
        <div className="grid w-full items-start overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-[0_30px_80px_rgba(15,23,42,0.45)] lg:grid-cols-[0.92fr_1.08fr]">
          <aside className="relative hidden lg:flex h-full flex-col justify-start gap-10 overflow-hidden bg-slate-900 px-8 py-9 text-white xl:gap-12">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(16,185,129,0.22),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(20,184,166,0.18),transparent_28%)]" />
            <div className="relative z-10">
              <div className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-slate-200 backdrop-blur">
                <Recycle className="h-4 w-4 text-emerald-400" />
                WasteZero Onboarding
              </div>
              <h1 className="mt-8 text-4xl font-bold leading-tight tracking-tight">
                Create a professional workspace in minutes.
              </h1>
              <p className="mt-4 max-w-md text-base leading-7 text-slate-300">
                Join the platform as a volunteer, NGO, agent, or admin and get access to the tools you need from day one.
              </p>
            </div>

            <div className="relative z-10 space-y-4">
              {onboardingHighlights.map((item) => (
                <div key={item} className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur-sm">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-300">
                      <ShieldCheck className="h-4 w-4" />
                    </div>
                    <p className="text-sm leading-6 text-slate-200">{item}</p>
                  </div>
                </div>
              ))}
            </div>
          </aside>

          <div className="flex flex-col justify-start px-6 py-8 sm:px-8 sm:py-10 lg:px-12">
            <div className="mb-8 flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                  Create Account
                </div>
                <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Join WasteZero</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Set up your account with password signup or email OTP verification.
                </p>
              </div>
              <div className="hidden h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 sm:flex">
                <UserPlus className="h-6 w-6" />
              </div>
            </div>

            {error && (
              <div className="mb-6 flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            {authMode === 'password' && verificationEmailSent && (
              <div className="mb-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
                <p className="text-sm font-medium">Verification link sent to {formData.email.trim()}.</p>
                <p className="mt-1 text-sm">Open your email, click the verification link, then sign in to access dashboard.</p>
                <button
                  type="button"
                  onClick={handleResendVerification}
                  disabled={isLoading}
                  className="mt-3 text-sm font-semibold text-emerald-700 hover:text-emerald-900 hover:underline disabled:opacity-60"
                >
                  Resend verification link
                </button>
              </div>
            )}

            <div className="mb-8 grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-1.5">
              <button
                type="button"
                onClick={() => {
                  setAuthMode('password');
                  setError('');
                  setOtpSent(false);
                  setOtp(['','','','','','']);
                }}
                className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  authMode === 'password'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Password Signup
              </button>
              <button
                type="button"
                onClick={() => {
                  setAuthMode('otp');
                  setError('');
                }}
                className={`rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ${
                  authMode === 'otp'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Email OTP Signup
              </button>
            </div>

            <form 
              onSubmit={authMode === 'password' ? handlePasswordSubmit : (e) => e.preventDefault()}
              className="space-y-6"
            >
              {/* Name */}
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Full Name
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                    <User className="h-5 w-5 text-slate-400" />
                  </div>

                  {/* NGO Certification Code - Only for NGO (password flow) */}
                  {formData.role === 'ngo' && (
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <label htmlFor="ngoCertificationCodePw" className="block text-sm font-medium text-blue-900 mb-1.5">
                        🏢 NGO Certification Code
                      </label>
                      <p className="text-xs text-blue-700 mb-2">Enter the certification code provided by WasteZero admins for your organization</p>
                      <input
                        id="ngoCertificationCodePw"
                        type="password"
                        name="ngoCertificationCode"
                        value={formData.ngoCertificationCode}
                        onChange={handleChange}
                        className="block w-full px-4 py-2.5 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter your NGO certification code"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {/* Admin Secret Key - Only for Admin (password flow) */}
                  {formData.role === 'admin' && (
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                      <label htmlFor="adminSecretKeyPw" className="block text-sm font-medium text-purple-900 mb-1.5">
                        👨‍💼 Admin Master Key
                      </label>
                      <p className="text-xs text-purple-700 mb-2">Enter the master key to register as a platform administrator</p>
                      <input
                        id="adminSecretKeyPw"
                        type="password"
                        name="adminSecretKey"
                        value={formData.adminSecretKey}
                        onChange={handleChange}
                        className="block w-full px-4 py-2.5 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter admin master key"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {/* Agent Secret Key - Only for Agent (password flow) */}
                  {formData.role === 'agent' && (
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <label htmlFor="agentSecretKeyPw" className="block text-sm font-medium text-amber-900 mb-1.5">
                        🚚 Agent Access Key
                      </label>
                      <p className="text-xs text-amber-700 mb-2">Enter the key provided by WasteZero admin to create an agent account</p>
                      <input
                        id="agentSecretKeyPw"
                        type="password"
                        name="agentSecretKey"
                        value={formData.agentSecretKey}
                        onChange={handleChange}
                        className="block w-full px-4 py-2.5 border border-amber-300 bg-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                        placeholder="Enter agent access key"
                        disabled={isLoading}
                      />
                    </div>
                  )}
                  <input
                    id="name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={inputWithIconClass}
                    placeholder="John Doe"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email address
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                    <Mail className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={inputWithIconClass}
                    placeholder="you@example.com"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              {authMode === 'password' ? (
                <>
                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="mb-1.5 block text-sm font-medium text-slate-700">
                      Password
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                        <Lock className="h-5 w-5 text-slate-400" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`${inputWithIconClass} pr-12`}
                        placeholder="••••••••"
                        required
                        minLength={6}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" />
                        ) : (
                          <Eye className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Role */}
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1.5">
                      I want to join as
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                        <Building2 className="h-5 w-5 text-slate-400" />
                      </div>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="block w-full appearance-none rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-slate-900 shadow-sm transition-all duration-200 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                        disabled={isLoading}
                      >
                        <option value="volunteer">🤝 Volunteer / Individual</option>
                        <option value="ngo">🏢 NGO / Organization</option>
                        <option value="agent">🚚 Agent / Collector</option>
                        <option value="admin">👨‍💼 Admin / Platform Manager</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className={primaryButtonClass}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      <>
                        Create Account
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* Role */}
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1.5">
                      I want to join as
                    </label>
                    <div className="relative">
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                        <Building2 className="h-5 w-5 text-slate-400" />
                      </div>
                      <select
                        id="role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                        className="block w-full appearance-none rounded-2xl border border-slate-300 bg-white py-3 pl-11 pr-4 text-slate-900 shadow-sm transition-all duration-200 focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-100"
                        disabled={isLoading}
                      >
                        <option value="volunteer">🤝 Volunteer / Individual</option>
                        <option value="ngo">🏢 NGO / Organization</option>
                        <option value="agent">🚚 Agent / Collector</option>
                        <option value="admin">👨‍💼 Admin / Platform Manager</option>
                      </select>
                    </div>
                  </div>

                  {/* NGO Certification Code - Only for NGO */}
                  {formData.role === 'ngo' && (
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <label htmlFor="ngoCertificationCodeOtp" className="block text-sm font-medium text-blue-900 mb-1.5">
                        🏢 NGO Certification Code
                      </label>
                      <p className="text-xs text-blue-700 mb-2">Enter the certification code provided by WasteZero admins for your organization</p>
                      <input
                        id="ngoCertificationCodeOtp"
                        type="password"
                        name="ngoCertificationCode"
                        value={formData.ngoCertificationCode}
                        onChange={handleChange}
                        className="block w-full px-4 py-2.5 border border-blue-300 bg-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="Enter your NGO certification code"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {/* Admin Secret Key - Only for Admin */}
                  {formData.role === 'admin' && (
                    <div className="p-4 rounded-lg bg-purple-50 border border-purple-200">
                      <label htmlFor="adminSecretKeyOtp" className="block text-sm font-medium text-purple-900 mb-1.5">
                        👨‍💼 Admin Master Key
                      </label>
                      <p className="text-xs text-purple-700 mb-2">Enter the master key to register as a platform administrator</p>
                      <input
                        id="adminSecretKeyOtp"
                        type="password"
                        name="adminSecretKey"
                        value={formData.adminSecretKey}
                        onChange={handleChange}
                        className="block w-full px-4 py-2.5 border border-purple-300 bg-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="Enter admin master key"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {/* Agent Secret Key - Only for Agent */}
                  {formData.role === 'agent' && (
                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <label htmlFor="agentSecretKeyOtp" className="block text-sm font-medium text-amber-900 mb-1.5">
                        🚚 Agent Access Key
                      </label>
                      <p className="text-xs text-amber-700 mb-2">Enter the key provided by WasteZero admin to create an agent account</p>
                      <input
                        id="agentSecretKeyOtp"
                        type="password"
                        name="agentSecretKey"
                        value={formData.agentSecretKey}
                        onChange={handleChange}
                        className="block w-full px-4 py-2.5 border border-amber-300 bg-white rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                        placeholder="Enter agent access key"
                        disabled={isLoading}
                      />
                    </div>
                  )}

                  {!otpSent ? (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isLoading || !formData.name.trim() || !formData.email.trim()}
                      className={primaryButtonClass}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        'Send OTP to Email'
                      )}
                    </button>
                  ) : (
                    <>
                      <div>
                        <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-slate-700">
                          <ShieldCheck className="h-5 w-5 text-emerald-600" />
                          Enter 6-digit OTP
                        </label>
                        <div className="flex justify-center gap-3">
                          {otp.map((digit, index) => (
                            <input
                              key={index}
                              id={`otp-${index}`}
                              type="text"
                              maxLength={1}
                              value={digit}
                              onChange={(e) => handleOtpChange(e.target.value, index)}
                              onKeyDown={(e) => handleOtpKeyDown(e, index)}
                              className={otpBoxClass}
                              autoComplete="one-time-code"
                              disabled={isLoading}
                            />
                          ))}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isLoading || otp.some(d => !d)}
                        className={primaryButtonClass}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Verifying...
                          </>
                        ) : (
                          'Verify & Create Account'
                        )}
                      </button>

                      <div className="text-center mt-4">
                        <button
                          type="button"
                          onClick={() => setOtpSent(false)}
                          className="text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-800 hover:underline"
                          disabled={isLoading}
                        >
                          Didn't receive OTP? Resend
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </form>

            <p className="mt-8 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-emerald-700 transition-colors hover:text-emerald-800 hover:underline"
              >
                Sign in
              </Link>
            </p>

            <p className="mt-8 text-center text-xs uppercase tracking-[0.18em] text-slate-400">
              Your data is secure • Join our mission for a cleaner Jalgaon
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}